/**
 * ProcessFormPreview — the request form as the person submitting it sees it.
 *
 * Why this exists: the Designer canvas is a STRUCTURE editor (order, width, which field is
 * selected). It cannot also be the fidelity check, and trying to make it both is what made it
 * loose and tall. The admin now gets one honest answer to "what will they actually fill in?"
 * before publishing.
 *
 * Drift control: the control chosen per field comes from `processFormFieldKind` — the SAME
 * function `WorkerSubmitRequest` uses — and the labels/options come from the same
 * `localizedProcessField*` helpers. A new field type therefore appears here and in the real
 * form together, and `processFormPreview.test.tsx` pins that every kind the contract can
 * return is handled.
 *
 * Honesty limits, deliberately visible in the copy:
 *   - every control is READ-ONLY; this previews layout and wording, it does not submit;
 *   - "Phone" is the worker portal at phone width, NOT a mock of the native mobile app —
 *     svdy-mobile renders its own screens, so claiming pixel parity would be a lie.
 */

import React from 'react';
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  PlaceOutlined as PlaceIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { ALWAYS_SHRINK, PORTAL_FORM_MAX_WIDTH } from '../../theme/shellMetrics';

import {
  attachmentFormats,
  dateFieldIncludesTime,
  localizedProcessFieldLabel,
  localizedProcessFieldOptions,
  localizedProcessFieldText,
  processFormFieldKey,
  processFormFieldKind,
  type ProcessFormFieldLike,
  type ProcessFormSchemaLike,
} from '../../pages/WorkerPortal/processFormContract';

export type PreviewFrame = 'desktop' | 'phone';

/** Worker-portal content width at a phone viewport, matching the portal's own gutters. */
export const PHONE_FRAME_WIDTH = 360;

export interface ProcessFormPreviewProps {
  schema: ProcessFormSchemaLike | null | undefined;
  /** Organization currency, so an amount field shows the code the worker will see. */
  currency?: string | null;
  frame?: PreviewFrame;
}

const ProcessFormPreview: React.FC<ProcessFormPreviewProps> = ({
  schema,
  currency,
  frame = 'desktop',
}) => {
  const { t } = useTranslation();
  const fields = Array.isArray(schema?.fields) ? schema!.fields! : [];

  if (fields.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('processes:formPreview.noFields')}
      </Typography>
    );
  }

  const renderField = (field: ProcessFormFieldLike, index: number) => {
    const key = processFormFieldKey(field, index);
    const label = localizedProcessFieldLabel(schema, field, index, t);
    const required = Boolean(field.properties?.required ?? field.required);
    const kind = processFormFieldKind(field.type);
    const shared = {
      key,
      label,
      required,
      fullWidth: true,
      size: 'small' as const,
      value: '',
      // Read-only rather than `disabled`: disabled greys the label out, which misrepresents
      // how the field will look to the worker.
      InputProps: { readOnly: true },
      onChange: () => undefined,
      // One label idiom for every field type. The date branch below used to pin
      // this alone, so dates showed a notched label and everything else showed its
      // label as body text inside the box — two kinds of control on one form
      // (owner, 2026-08-23).
      ...ALWAYS_SHRINK,
    };

    if (kind === 'staticText') {
      // Not a field. It collects nothing, so it must not be drawn as one — a
      // disabled input would tell the admin the requester can interact with it.
      //
      // The LABEL is shown as a heading because both worker clients show it
      // (`WorkerSubmitRequest`, `svdy-mobile/app/submit-request.tsx`), and this
      // component's whole claim is "what will they actually fill in?" — omitting a
      // line the requester sees makes the preview quietly wrong.
      const text = localizedProcessFieldText(schema, field, t);
      return (
        <Box key={key}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>{label}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
            {text}
          </Typography>
        </Box>
      );
    }
    if (kind === 'worker' || kind === 'department') {
      // The same shape the `location` branch below uses: a picker the requester
      // chooses from, whose value the server resolves and snapshots.
      return (
        <TextField {...shared} select SelectProps={{ readOnly: true }}>
          <MenuItem value="" />
        </TextField>
      );
    }
    if (kind === 'place') {
      // Read-only like every control here. Drawn as a plain box on purpose: the real
      // form's autocomplete is a client capability keyed on a Google key, so promising
      // a dropdown in the preview would over-claim on a tenant that has none.
      return <TextField {...shared} helperText={t('processes:formPreview.placeHelper')} />;
    }
    if (kind === 'textarea') {
      return <TextField {...shared} multiline minRows={3} />;
    }
    if (kind === 'number' || kind === 'currency') {
      const code = String(field.properties?.currency ?? currency ?? 'USD').toUpperCase();
      return (
        <TextField
          {...shared}
          type="number"
          InputProps={{
            readOnly: true,
            ...(kind === 'currency'
              ? { endAdornment: <InputAdornment position="end">{code}</InputAdornment> }
              : {}),
          }}
        />
      );
    }
    if (kind === 'date') {
      // `datetime-local` is fine HERE — the preview is read-only, so the browser's own
      // calendar locale never opens.
      return <TextField {...shared} type={dateFieldIncludesTime(field) ? 'datetime-local' : 'date'} />;
    }
    if (kind === 'select') {
      const options = localizedProcessFieldOptions(schema, field, t);
      return (
        <TextField {...shared} select SelectProps={{ readOnly: true }}>
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
      );
    }
    if (kind === 'multiSelect') {
      const options = localizedProcessFieldOptions(schema, field, t);
      return (
        <TextField
          {...shared}
          select
          value={[]}
          SelectProps={{
            readOnly: true,
            multiple: true,
            // The preview's job is to show the admin WHAT the requester will see, and for
            // a multi-select that is "several of these", not one of them. Rendering it as
            // a single-choice control here is how the two got confused in the palette.
            renderValue: () => t('processes:formPreview.multiSelectEmpty'),
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
      );
    }
    if (kind === 'duration') {
      // Shows the FORMAT rather than a value, because that is what the admin is checking:
      // whether the field they placed asks for what they meant. The stored unit is
      // minutes; the control accepts `2:30` too.
      return (
        <TextField
          {...shared}
          placeholder={t('processes:formPreview.durationPlaceholder')}
          helperText={t('processes:formPreview.durationHelper')}
        />
      );
    }
    if (kind === 'location') {
      return (
        <TextField
          {...shared}
          select
          SelectProps={{ readOnly: true }}
          helperText={t('processes:formPreview.locationHelper')}
        >
          <MenuItem value="">
            <PlaceIcon fontSize="small" sx={{ mr: 0.5, color: 'text.disabled' }} />
            {t('processes:formPreview.locationPlaceholder')}
          </MenuItem>
        </TextField>
      );
    }
    if (kind === 'attachment') {
      return (
        <Box key={key}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
            {label}
            {required && <Typography component="span" color="error.main"> *</Typography>}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AttachFileIcon />}
            disabled
            sx={{ textTransform: 'none' }}
          >
            {t('processes:formPreview.attachmentPlaceholder')}
          </Button>
          {/* The formats the worker will actually be offered, read from the same contract the
              worker's picker and the backend validator use. */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {attachmentFormats(field.properties?.allowedTypes)
              .map((format) => t(`processes:wizardForm.fileFormats.${format}`))
              .join(' · ')}
          </Typography>
        </Box>
      );
    }
    if (kind === 'unsupported') {
      // Never silently drop it: a field the runtime cannot render is exactly what the admin
      // needs to see BEFORE publishing, because the worker's form would omit it too.
      return (
        <Typography key={key} variant="caption" color="error.main">
          {t('processes:formPreview.unsupportedField', { label })}
        </Typography>
      );
    }
    return <TextField {...shared} />;
  };

  return (
    <Box
      data-testid={`process-form-preview-${frame}`}
      sx={{
        // Both frames draw the card the requester actually sees. Desktop used to
        // draw none, on the reasoning that the Review card already provides one
        // (svdy-ui §15). The concern is real but it was aimed at the wrong
        // question: the portal's form IS a bordered Paper at PORTAL_FORM_MAX_WIDTH
        // (`WorkerSubmitRequest.tsx`), so a full-width borderless stack is a
        // different layout rather than a cleaner one — the owner read it as
        // "缺少了边框约束和紧凑型". The Review section around this adds no card of
        // its own, so there is only one edge either way.
        width: frame === 'phone' ? PHONE_FRAME_WIDTH : PORTAL_FORM_MAX_WIDTH,
        maxWidth: '100%',
        mx: frame === 'phone' ? 'auto' : 0,
        p: frame === 'phone' ? 2 : 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* 1.75 → 1.25: at `size="small"` with the label in the notch, 14px between
          fields is the portal's own rhythm. The looser value dated from when the
          labels sat inside the boxes and needed the extra separation. */}
      {/* A 12-column grid, because `properties.colSpan` is a real layout instruction and
          this renderer used to ignore it.
          
          Owner screenshot 2026-08-28: two fields were set to Half in the template dialog and
          this view — the template detail preview — stacked all four full width. Measured
          across the app, `colSpan` was read by the DESIGNER CANVAS ALONE: this component
          (which also backs the Designer's Review step), the worker portal form and the mobile
          form all rendered everything full width. So "half width" moved boxes on an admin's
          design surface and nothing a requester ever saw, which is the "capability with no
          consumer" defect rather than a missing control.
          
          RESPONSIVE, not fixed: `span 12` below the `sm` breakpoint. `colSpan: 6` means "half
          width where there is room", and a phone never has room — two side-by-side inputs at
          390px are worse than one per row. That is also why the mobile form needs no change:
          it is the narrow case of one rule, not an omission. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          columnGap: 1.5,
          rowGap: 1.25,
        }}
      >
        {fields.map((field, index) => {
          const raw = Number((field.properties as Record<string, unknown> | undefined)?.colSpan);
          // Anything outside the grid falls back to full width rather than rendering a
          // sliver: a legacy field has no span at all, and a hand-written schema could
          // carry any number.
          const span = raw >= 1 && raw <= 12 ? raw : 12;
          return (
            <Box
              key={`${index}-${span}`}
              // Exposed PURELY so the invariant can be asserted. jsdom's
              // `getComputedStyle` does not evaluate media queries, so the responsive
              // `sm` branch below is invisible to a computed-style check — it would read
              // `span 12` for every cell and pass on a form where the span never
              // arrived, which is exactly the defect (owner screenshots 2026-08-28).
              // `templateDetailWidth.test.tsx` counts these.
              data-col-span={span}
              sx={{ gridColumn: { xs: 'span 12', sm: `span ${span}` }, minWidth: 0 }}
            >
              {renderField(field, index)}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ProcessFormPreview;
